import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './Auth.module.css';
import { useResetPasswordMutation } from '../../store/api/authApi';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import Logo from '../../components/Logo';

const ResetPassword = () => {
  const [formData, setFormData] = useState({ newPassword: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const togglePassword = () => setShowPassword((prev) => !prev);

  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email');
  const token = queryParams.get('token');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError('');
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    const { newPassword, confirmPassword } = formData;
    const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

    if (!strongPasswordRegex.test(newPassword)) {
      setError("Password must be at least 8 characters and include uppercase, lowercase, number, and special character.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      const response = await resetPassword({ email, token: encodeURIComponent(token), newPassword });
      setSuccess(response.message || "Password reset successfully.");
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setError(err?.data?.message || err?.message || "Failed to reset password. Please try again.");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>

        {/* ── Left brand panel ── */}
        <div className={styles.brandPanel}>
          <div className={styles.brandInner}>
            <Logo height={100} scale={3} />
            <p className={styles.brandTagline}>
              Create a strong new password to keep your account and your circle safe.
            </p>
            <div className={styles.brandIcon}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="48" height="48" rx="14" fill="rgba(251,152,51,0.15)" />
                <path d="M24 14a6 6 0 0 1 6 6v2H18v-2a6 6 0 0 1 6-6z" stroke="#fb9833" strokeWidth="2" strokeLinejoin="round" fill="none"/>
                <rect x="14" y="22" width="20" height="14" rx="3" stroke="#fb9833" strokeWidth="2" fill="none"/>
                <path d="M21 29l2 2 4-4" stroke="#fb9833" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className={styles.brandDots}>
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.dot} />
            </div>
          </div>
        </div>

        {/* ── Right form panel ── */}
        <div className={styles.formPanel}>
          <div className={styles.formInner}>
            <div className={styles.formHeader}>
              <h2 className={styles.heading}>Reset Password</h2>
              <p className={styles.subheading}>
                Set a new password for your Nessefy account
              </p>
            </div>

            {error && <p className={styles.error}>{error}</p>}
            {success && <p className={styles.successMsg}>{success}</p>}

            <form onSubmit={handleSubmit} noValidate>
              <div className={styles.formGroup}>

                <div className={styles.fieldGroup}>
                  <label className={styles.label} htmlFor="newPassword">New Password</label>
                  <div className={styles.passwordWrapper}>
                    <input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      name="newPassword"
                      placeholder="Enter new password"
                      value={formData.newPassword}
                      onChange={handleChange}
                      required disabled={isLoading}
                      className={styles.input}
                    />
                    <span className={styles.eyeIcon} onClick={togglePassword}>
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </div>
                </div>

                <div className={styles.fieldGroup}>
                  <label className={styles.label} htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    name="confirmPassword"
                    placeholder="Repeat new password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required disabled={isLoading}
                    className={styles.input}
                  />
                </div>

                <ul className={styles.passwordHints}>
                  <li className={/[A-Z]/.test(formData.newPassword) ? styles.hintMet : ''}>Uppercase letter</li>
                  <li className={/[a-z]/.test(formData.newPassword) ? styles.hintMet : ''}>Lowercase letter</li>
                  <li className={/\d/.test(formData.newPassword) ? styles.hintMet : ''}>Number</li>
                  <li className={/[\W_]/.test(formData.newPassword) ? styles.hintMet : ''}>Special character</li>
                  <li className={formData.newPassword.length >= 8 ? styles.hintMet : ''}>At least 8 characters</li>
                </ul>

                <button type="submit" className={styles.button} disabled={isLoading}>
                  {isLoading ? 'Resetting…' : 'Reset Password'}
                </button>

              </div>
            </form>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ResetPassword;